/**
 * Batchnings-helpers för snap-to-road mot GraphHopper-proxyn (S-04-04b
 * follow-up, 2026-05-08).
 *
 * Bakgrund: GraphHopper free-tier tillåter max 5 waypoints per request
 * (verifierat empiriskt mot Compuna-kontot 2026-05-08 — svar
 * `{"message":"Too many points for Routing API: 32, allowed: 5"}`). En
 * runda i Lyktan kan vara 50-100 klick eftersom admin vill bevara klick-
 * precisionen för täckningsdata. Vi delar därför upp anropet i batcher
 * om upp till 5 punkter och sätter ihop polylinerna.
 *
 * Hard rules respected:
 * - Pure helpers — ingen fetch, ingen sidoeffekt. All nätverkslogik
 *   ligger kvar i `routeService.ts` och konsumerar dessa hjälpare. Det
 *   låter testerna verifiera batchningens matematik utan att stuba HTTP.
 * - Overlap = 1 punkt mellan batcher. Sista punkten i batch N skickas som
 *   första punkt i batch N+1 så GraphHopper alltid har en startposition.
 *   När polylinerna sätts ihop droppar vi första punkten i nästkommande
 *   polyline (= dubbletten av föregående batchs slutpunkt) så slutkurvan
 *   blir sömlös.
 * - Aldrig batcha redan validerade gränser bort: batchningen antar att
 *   `validateWaypoints` redan körts och att alla punkter är inom WGS84.
 */

import type { Coordinate, Polyline } from './types';

/**
 * Övre tak för antal waypoints per enskilt GraphHopper-anrop. Sätts till
 * GraphHopper free-tier-taket (5). Backend tillåter 2..100 — om Compuna
 * uppgraderar GraphHopper-prenumerationen och taket höjs är det den här
 * konstanten som ska bytas. Lägre tak fungerar också; det blir bara fler
 * batcher per snap.
 */
export const SNAP_MAX_WAYPOINTS_PER_REQUEST = 5;

/**
 * Throttle mellan batcher. Sätts till 1500ms (~40 anrop/min sustained)
 * efter empirisk verifiering 2026-05-08: 100ms throttle gav 429
 * "Minutely API limit heavily violated" från GraphHopper free-tier
 * efter ~9 anrop på under en sekund. 1500ms håller oss under både
 * GraphHopper-minute-takt och backend-rate-limit (`maxRequestsPerMinute = 60`
 * per admin i `RoutingProxy.kt`). En 100-klicks-runda blir ~25 batcher
 * → ~37s totalt. Acceptabelt UX för en sällan-uppgift; admin ser
 * progress-räknaren ticka i UI:t.
 */
export const BATCH_THROTTLE_MS = 1500;

/**
 * Information som UI:t kan visa under en pågående batch-snap. Skickas
 * till `onProgress`-callbacken före varje delanrop så användaren ser att
 * något händer även när rundan tar några sekunder att snap:a.
 */
export interface BatchProgress {
  readonly batchIndex: number;
  readonly totalBatches: number;
}

/**
 * Dela en lista waypoints i batcher om upp till `batchSize` punkter där
 * sista punkten i batch N återanvänds som första punkt i batch N+1.
 * Returnerar alltid minst en batch (även för 2-punkts-input) och
 * garanterar att varje batch har minst 2 punkter (matchar GraphHopper:s
 * minkrav per anrop).
 *
 * Edge cases:
 * - `waypoints.length <= batchSize`: returnerar [waypoints] (1 batch).
 * - `waypoints.length < 2`: returnerar tom array — caller är ansvarig
 *   för att inte anropa med < 2 punkter (validateWaypoints fångar det).
 * - `batchSize < 2`: kastar — det är ett programmeringsfel, inte ett
 *   körtidsläge vi vill att fallback ska tysta.
 *
 * Algoritmen i pseudokod:
 *   i = 0
 *   while i < N - 1:
 *     end = min(i + batchSize, N)
 *     emit slice(i, end)
 *     if end >= N: break
 *     i = end - 1   // overlap = 1 punkt
 *
 * Exempel med batchSize=5:
 *   N=2  → [[0,1]]
 *   N=5  → [[0,1,2,3,4]]
 *   N=6  → [[0,1,2,3,4], [4,5]]
 *   N=10 → [[0..4], [4..8], [8,9]]
 *   N=33 → 8 batcher
 */
export function batchWaypoints(
  waypoints: ReadonlyArray<Coordinate>,
  batchSize: number = SNAP_MAX_WAYPOINTS_PER_REQUEST,
): ReadonlyArray<ReadonlyArray<Coordinate>> {
  if (batchSize < 2) {
    throw new Error(`batchSize måste vara >= 2 (var ${batchSize})`);
  }
  if (waypoints.length < 2) return [];
  if (waypoints.length <= batchSize) return [waypoints];

  const batches: Coordinate[][] = [];
  let i = 0;
  while (i < waypoints.length - 1) {
    const end = Math.min(i + batchSize, waypoints.length);
    batches.push(waypoints.slice(i, end) as Coordinate[]);
    if (end >= waypoints.length) break;
    i = end - 1;
  }
  return batches;
}

/**
 * Sätt ihop polylines från batchade snap-anrop till en sammanhängande
 * polyline. Drop:ar första punkten i alla utom första polyline eftersom
 * den är dubbletten av föregående batchs slutpunkt (overlap-strategin).
 *
 * Edge cases:
 * - Tom inmatning: kastar. Pure-funktion, caller måste säkerställa minst
 *   en polyline.
 * - En polyline: returnerar oförändrad.
 *
 * Notera: den här funktionen verifierar inte att skarvarna verkligen
 * matchar (sista punkten i N == första i N+1). Om GraphHopper väljer
 * olika rutter mellan batcherna kan slutkurvan ha små diskontinuiteter
 * vid skarvarna — det är acceptabelt för MVP1 eftersom täckningsdata
 * och visualisering tål några meter avvikelse. Strikt matching skulle
 * kräva explicit waypoint-passage hos GraphHopper vilket inte är värt
 * komplexiteten just nu.
 */
export function concatPolylines(polylines: ReadonlyArray<Polyline>): Polyline {
  if (polylines.length === 0) {
    throw new Error('concatPolylines anropad med tom lista');
  }
  if (polylines.length === 1) return polylines[0];

  const points: Coordinate[] = [...polylines[0].points];
  for (let i = 1; i < polylines.length; i++) {
    points.push(...polylines[i].points.slice(1));
  }
  return { points };
}
